<template>
  <div class="home-stage">
    <canvas ref="canvas" class="bg-canvas"></canvas>

    <div class="hero-center" :class="{ 'hero-center--chat': chatMode }">
      <p class="hero-caption">Hello! I'm Jroge, how can I help you? :D</p>

      <div class="chips">
        <button
          v-for="cat in categories"
          :key="cat"
          class="chip"
          :class="{ active: activeCategory === (cat === 'All' ? null : cat) }"
          @click="setCategory(cat)"
        >{{ cat }}</button>
      </div>

      <div v-if="chatMode" class="chat-messages" ref="chatMessages">
        <template v-for="(msg, i) in messages" :key="i">
          <div v-if="msg.role === 'user'" class="chat-bubble chat-bubble--user">{{ msg.text }}</div>
          <div
            v-else
            class="chat-bubble chat-bubble--assistant"
            :class="{ 'chat-bubble--error': msg.isError }"
            v-html="renderMarkdown(msg.text)"
          ></div>
        </template>
        <div v-if="isLoading" class="chat-bubble chat-bubble--assistant chat-bubble--thinking">
          Thinking...
        </div>
      </div>

      <transition v-else name="notice">
        <div v-if="isLoading" class="maintenance-notice">
          <span>Thinking...</span>
        </div>
      </transition>

      <input
        v-model="query"
        type="text"
        class="hero-input"
        placeholder="Ask me anything..."
        :disabled="isLoading"
        @keyup.enter="handleQuery"
      />

      <div class="bottom-row">
        <p class="cv-hint">
          Looking for my CV?
          <a href="/Jorge-Torrez-CV.pdf" target="_blank" download class="cv-link">Click here.</a>
        </p>
        <div class="socials">
          <a href="https://www.linkedin.com/in/jorgerodrigotorrez/" target="_blank" class="social-icon" title="LinkedIn"><i class="bi bi-linkedin"></i></a>
          <a href="https://github.com/JrogeT" target="_blank" class="social-icon" title="GitHub"><i class="bi bi-github"></i></a>
          <a href="mailto:jorgerodrigotorrez@gmail.com" class="social-icon" title="Gmail"><i class="bi bi-envelope-fill"></i></a>
          <a href="https://wa.me/595987260520" target="_blank" class="social-icon" title="WhatsApp"><i class="bi bi-whatsapp"></i></a>
          <a href="https://www.facebook.com/JorgeRodrigoTorrezAramayo" target="_blank" class="social-icon" title="Facebook"><i class="bi bi-facebook"></i></a>
          <a href="https://www.instagram.com/jroge_t" target="_blank" class="social-icon" title="Instagram"><i class="bi bi-instagram"></i></a>
          <a href="https://www.youtube.com/channel/UCSLi91PW9oH9LETcTsDs-5Q" target="_blank" class="social-icon" title="YouTube"><i class="bi bi-youtube"></i></a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ITEMS } from '@/canvas/canvasData'
import { preloadImages } from '@/canvas/imageLoader'
import { makeTriangle, makeDecorativeTriangles, resolveCollisions, repositionForChat, easeOutCubic } from '@/canvas/triangleFactory'
import { renderMarkdown } from '@/utils/markdown'
import { drawTriangle, drawLabel } from '@/canvas/canvasRenderer'

export default {
  name: 'AppHome',
  data() {
    return {
      query: '',
      activeCategory: null,
      isLoading: false,
      messages: [],
      categories: ['All', 'Web Development', 'Sports', 'Hobbies'],
    }
  },
  computed: {
    chatMode() { return this.messages.length > 0 },
  },
  watch: {
    chatMode(val) {
      if (!val) return
      const canvas = this.$refs.canvas
      const cx = canvas.width / 2
      const cy = canvas.height / 2
      repositionForChat(
        [...this._decorativeTriangles, ...this._triangles],
        canvas.width, canvas.height, cx, cy
      )
    },
  },
  mounted() {
    this._animId = null
    this._triangles = []
    this._decorativeTriangles = []
    this._mouseX = -9999
    this._mouseY = -9999
    preloadImages(() => this.initCanvas())
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    cancelAnimationFrame(this._animId)
    window.removeEventListener('resize', this.onResize)
    const canvas = this.$refs.canvas
    if (canvas) {
      canvas.removeEventListener('mousemove', this.onMouseMove)
      canvas.removeEventListener('mouseleave', this.onMouseLeave)
    }
  },
  methods: {
    initCanvas() {
      cancelAnimationFrame(this._animId)
      const canvas = this.$refs.canvas
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      canvas.addEventListener('mousemove', this.onMouseMove)
      canvas.addEventListener('mouseleave', this.onMouseLeave)
      const cx = canvas.width / 2
      const cy = canvas.height / 2
      this._decorativeTriangles = makeDecorativeTriangles(cx, cy, canvas.width, canvas.height)
      this._triangles = ITEMS.map((item, i) =>
        makeTriangle(cx, cy, canvas.width, canvas.height, item, i, ITEMS.length)
      )
      resolveCollisions(
        [...this._decorativeTriangles, ...this._triangles],
        canvas.width, canvas.height, cx, cy
      )
      if (this.chatMode) {
        repositionForChat(
          [...this._decorativeTriangles, ...this._triangles],
          canvas.width, canvas.height, cx, cy
        )
      }
      this.animate()
    },
    onResize() { this.initCanvas() },
    onMouseMove(e) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      this._mouseX = e.clientX - rect.left
      this._mouseY = e.clientY - rect.top
    },
    onMouseLeave() {
      this._mouseX = -9999
      this._mouseY = -9999
    },
    setCategory(cat) {
      this.activeCategory = cat === 'All' ? null : cat
    },
    renderMarkdown,
    scrollToBottom() {
      const el = this.$refs.chatMessages
      if (el) el.scrollTop = el.scrollHeight
    },
    animate() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const now = performance.now() / 1000

      for (const t of this._decorativeTriangles) {
        if (t.progress < 1) t.progress = Math.min(1, t.progress + 0.018)
        const ease = easeOutCubic(t.progress)
        if (t.progress < 1) {
          t.x = t.startX + (t.targetX - t.startX) * ease
          t.y = t.startY + (t.targetY - t.startY) * ease
        } else {
          t.x = t.targetX + Math.sin(now * t.idleFreqX + t.idlePhaseX) * t.idleAmp
          t.y = t.targetY + Math.cos(now * t.idleFreqY + t.idlePhaseY) * t.idleAmp
        }
        t.rotation += t.rotSpeed
        drawTriangle(ctx, t)
      }

      for (const t of this._triangles) {
        if (t.progress < 1) t.progress = Math.min(1, t.progress + 0.024)
        const ease = easeOutCubic(t.progress)

        if (t.progress < 1) {
          t.x = t.startX + (t.targetX - t.startX) * ease
          t.y = t.startY + (t.targetY - t.startY) * ease
        } else {
          t.x = t.targetX + Math.sin(now * t.idleFreqX + t.idlePhaseX) * t.idleAmp
          t.y = t.targetY + Math.cos(now * t.idleFreqY + t.idlePhaseY) * t.idleAmp
        }

        const spin = t.progress < 1
          ? t.rotSpeed * (1 - ease * 0.85)
          : t.rotSpeed * 0.04
        t.rotation += spin

        const targetAlpha = !this.activeCategory || t.category === this.activeCategory ? 1 : 0.12
        t.currentAlpha += (targetAlpha - t.currentAlpha) * 0.07

        drawTriangle(ctx, t)
      }

      const mx = this._mouseX, my = this._mouseY
      let hovered = null
      for (const t of this._triangles) {
        const dx = mx - t.x, dy = my - t.y
        if (Math.sqrt(dx * dx + dy * dy) < t.size) { hovered = t; break }
      }
      for (const t of this._triangles) {
        if ((this.activeCategory && t.category === this.activeCategory) || t === hovered) {
          drawLabel(ctx, t)
        }
      }

      this._animId = requestAnimationFrame(this.animate)
    },
    async handleQuery() {
      const question = this.query.trim()
      if (!question || this.isLoading) return
      this.messages.push({ role: 'user', text: question })
      this.query = ''
      this.isLoading = true
      await this.$nextTick()
      this.scrollToBottom()
      try {
        const AI_URL = process.env.AI_API_URL || 'http://localhost:8000'
        const res = await fetch(`${AI_URL}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.detail || 'Request failed')
        this.messages.push({ role: 'assistant', text: data.answer })
      } catch {
        this.messages.push({ role: 'assistant', text: "Something went wrong, I'm so sorry! 🙏", isError: true })
      } finally {
        this.isLoading = false
        await this.$nextTick()
        this.scrollToBottom()
      }
    },
  },
}
</script>

<style scoped>
.home-stage {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #ffffff;
}

.bg-canvas {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  display: block;
}

.hero-center {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  text-align: center;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  padding: 30px 52px;
  min-width: 580px;
  max-width: 90vw;
  box-shadow: 0 6px 36px rgba(0, 0, 0, 0.08);
  transition: min-width 0.35s ease, max-width 0.35s ease;
}

.hero-center--chat {
  min-width: 740px;
  max-width: 94vw;
}

.hero-caption {
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 16px;
  letter-spacing: 0.01em;
  line-height: 1.5;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-bottom: 16px;
}

.chip {
  padding: 4px 13px;
  border-radius: 50px;
  border: 1.5px solid #d1d5db;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
  line-height: 1;
}

.chip:hover {
  border-color: #374151;
  color: #374151;
}

.chip.active {
  background: #1a1a1a;
  border-color: #1a1a1a;
  color: #ffffff;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  margin-bottom: 12px;
  padding: 4px 2px;
  text-align: left;
  scroll-behavior: smooth;
}

.chat-bubble {
  max-width: 78%;
  padding: 9px 14px;
  border-radius: 16px;
  font-size: 0.82rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-bubble--user {
  align-self: flex-end;
  background: #1a1a1a;
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.chat-bubble--assistant {
  align-self: flex-start;
  background: #f3f4f6;
  color: #1a1a1a;
  border-bottom-left-radius: 4px;
  white-space: normal;
}

.chat-bubble--assistant :deep(p) { margin: 0 0 6px; }
.chat-bubble--assistant :deep(p:last-child) { margin-bottom: 0; }
.chat-bubble--assistant :deep(strong) { font-weight: 700; }
.chat-bubble--assistant :deep(em) { font-style: italic; }
.chat-bubble--assistant :deep(a) { color: #374151; }
.chat-bubble--assistant :deep(ul),
.chat-bubble--assistant :deep(ol) { padding-left: 18px; margin: 4px 0; }
.chat-bubble--assistant :deep(li) { margin: 2px 0; }
.chat-bubble--assistant :deep(h1),
.chat-bubble--assistant :deep(h2),
.chat-bubble--assistant :deep(h3) { font-size: 0.88rem; font-weight: 700; margin: 8px 0 4px; }
.chat-bubble--assistant :deep(code) { background: rgba(0,0,0,0.07); padding: 1px 5px; border-radius: 4px; font-size: 0.78em; }
.chat-bubble--assistant :deep(pre) { background: rgba(0,0,0,0.06); padding: 8px 10px; border-radius: 6px; overflow-x: auto; margin: 6px 0; }
.chat-bubble--assistant :deep(pre code) { background: none; padding: 0; }

.chat-bubble--error {
  background: #fef2f2;
  color: #991b1b;
}

.chat-bubble--thinking {
  opacity: 0.5;
  font-style: italic;
}

.hero-input {
  width: 100%;
  background: #f9fafb;
  border: 1.5px solid #d1d5db;
  border-radius: 50px;
  padding: 10px 20px;
  color: #1a1a1a;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.3s, box-shadow 0.3s;
  box-sizing: border-box;
}

.hero-input::placeholder { color: #9ca3af; }

.hero-input:focus {
  border-color: #374151;
  box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1);
}

.maintenance-notice {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 0;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1.5px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #374151;
}

.notice-enter-active, .notice-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.notice-enter-from, .notice-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.bottom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
}

.cv-hint {
  margin: 0;
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
}

.cv-link {
  color: #374151;
  text-decoration: none;
  font-weight: 600;
}

.cv-link:hover { text-decoration: underline; }

.socials {
  display: flex;
  gap: 12px;
  align-items: center;
}

.social-icon {
  color: #9ca3af;
  font-size: 1rem;
  transition: color 0.2s;
  line-height: 1;
}

.social-icon:hover { color: #1a1a1a; }

@media (max-width: 600px) {
  .hero-center {
    min-width: unset;
    width: calc(100% - 32px);
    padding: 20px 20px;
    border-radius: 16px;
  }

  .hero-center--chat {
    min-width: unset;
    max-height: 85vh;
  }

  .hero-caption {
    font-size: 0.9rem;
  }

  .chat-messages {
    max-height: 260px;
  }

  .bottom-row {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .cv-hint {
    white-space: normal;
    text-align: center;
  }

  .socials {
    gap: 16px;
  }

  .social-icon {
    font-size: 1.2rem;
  }
}
</style>
